import { GenericLabelValue } from 'types/common';
import { ALL_MAINTENANCE_STATUS_VALUES, EQUIPMENT_STATUS_VALUES } from '../constants';

const STORAGE_KEY = 'maintenanceFilters';

// Bump whenever the stored shape changes - a mismatch falls back to the defaults instead of
// feeding a stale shape into the filters.
const STORAGE_VERSION = 1;

// Bounds both the stored payload and the query string used to resolve the ids on load.
const MAX_RETAINED_EQUIPMENT_IDS = 25;

interface MaintenanceFilterSelection {
  status: GenericLabelValue<string>[];
  equipmentStatus: GenericLabelValue<number>[];
  // Employees and task templates are stored as label and value only, never whole API records.
  // There is no by-ids lookup on those endpoints so they cannot be rehydrated: a renamed one keeps
  // showing its old label, and a deleted one keeps filtering by a dead id until the user changes
  // the selection. Fixing that needs an ids filter on the employee and template list endpoints.
  employees: GenericLabelValue[];
  taskTemplates: GenericLabelValue[];
}

// Equipment is stored as ids only and rehydrated on load, because the dropdown options carry live
// meter readings (TotalEquipTime / TotalEquipmentTime / MilesTravelled) that prefill the current
// hours/miles fields in Assign Maintenance Task. Storing whole options would cache readings that go
// stale immediately.
export interface StoredMaintenanceFilters extends MaintenanceFilterSelection {
  equipmentIds: string[];
}

// Equipment ids are numbers at runtime even though the filter option type says string, so writes
// accept either and are normalised on the way in.
export interface MaintenanceFiltersToStore extends MaintenanceFilterSelection {
  equipmentIds: (string | number)[];
}

interface StoragePayload extends MaintenanceFiltersToStore {
  version: number;
  companyId: number;
}

const isLabelValue = (option: unknown): option is GenericLabelValue<unknown> =>
  typeof option === 'object' &&
  option !== null &&
  'label' in option &&
  'value' in option &&
  (option as GenericLabelValue<unknown>).value !== undefined;

// Strips anything beyond label and value so the payload cannot grow into whole API records - task
// template options spread their entire record.
const toLabelValue = <T>(options: GenericLabelValue<T>[]) =>
  options.map(({ label, value }) => ({ label, value }));

const toEquipmentIds = (ids: (string | number)[]) => ids.map(String);

const isValidId = (id: unknown) => typeof id === 'string' || typeof id === 'number';

const isValidOptionList = (value: unknown, allowedValues?: (string | number)[]) =>
  Array.isArray(value) &&
  value.every(
    option =>
      isLabelValue(option) &&
      (!allowedValues || allowedValues.includes(option.value as string | number))
  );

// The payload is stamped with the company it was saved for and ignored when it does not match the
// active company, so switching companies starts from the defaults instead of carrying another
// company's ids across. Anything that fails validation discards the whole payload rather than
// being repaired. Status values are whitelisted because they go straight into a query string.
export const readStoredMaintenanceFilters = (
  companyId: number
): StoredMaintenanceFilters | undefined => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);

    if (!raw) return undefined;

    const parsed: StoragePayload = JSON.parse(raw);

    if (parsed?.version !== STORAGE_VERSION || parsed?.companyId !== companyId) return undefined;

    if (
      !isValidOptionList(parsed.status, ALL_MAINTENANCE_STATUS_VALUES) ||
      !isValidOptionList(parsed.equipmentStatus, EQUIPMENT_STATUS_VALUES) ||
      !isValidOptionList(parsed.employees) ||
      !isValidOptionList(parsed.taskTemplates) ||
      !Array.isArray(parsed.equipmentIds) ||
      !parsed.equipmentIds.every(isValidId)
    ) {
      return undefined;
    }

    return {
      status: toLabelValue(parsed.status),
      equipmentStatus: toLabelValue(parsed.equipmentStatus),
      equipmentIds: toEquipmentIds(parsed.equipmentIds).slice(0, MAX_RETAINED_EQUIPMENT_IDS),
      // Also normalises payloads written before options were reduced to label and value.
      employees: toLabelValue(parsed.employees),
      taskTemplates: toLabelValue(parsed.taskTemplates)
    };
  } catch (error) {
    // Corrupt payload - fall back to the defaults.
    return undefined;
  }
};

export const writeStoredMaintenanceFilters = (
  companyId: number,
  filters: MaintenanceFiltersToStore
) => {
  try {
    const payload: StoragePayload = {
      version: STORAGE_VERSION,
      companyId,
      status: toLabelValue(filters.status),
      equipmentStatus: toLabelValue(filters.equipmentStatus),
      equipmentIds: toEquipmentIds(filters.equipmentIds).slice(0, MAX_RETAINED_EQUIPMENT_IDS),
      employees: toLabelValue(filters.employees),
      taskTemplates: toLabelValue(filters.taskTemplates)
    };

    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch (error) {
    // Storage full or unavailable - retention is best effort, never block the UI.
  }
};

export const removeStoredMaintenanceFilters = () => {
  localStorage.removeItem(STORAGE_KEY);
};
